import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";

import * as React from "react";

import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

import FontFamilyCombobox, { useFontItems } from "./FontFamilyCombobox";

interface FontSelectorProps {
  fontFamily: string | null;
  onSelect: (fontValue: string) => void;
  placeholder: string;
}

const FontSelector: React.FC<FontSelectorProps> = ({
  fontFamily,
  onSelect,
  placeholder,
}) => {
  const { projectFontSet } = useFontItems();

  const [fontPreviewErrors, setFontPreviewErrors] = React.useState<
    Record<string, boolean>
  >({});

  const [displayValue, setDisplayValue] = useOverridableState(
    fontFamily ?? "",
    onSelect,
  );

  // Normalize font family for display
  const normalizedFontFamily = displayValue
    ? displayValue.split(",")[0]?.trim().replace(/["']/g, "")
    : null;

  const renderItemLabel = React.useCallback(
    (item: ComboboxItemWithValue) => {
      const isGoogleFont = !projectFontSet.has(item.value);
      if (isGoogleFont && !fontPreviewErrors[item.value]) {
        return (
          <img
            src={`/images/font-previews/${item.value.replace(/\s+/g, "_")}.webp`}
            alt={String(item.label)}
            width={182}
            height={16}
            loading="lazy"
            onError={() => {
              return setFontPreviewErrors((prev) => ({
                ...prev,
                [item.value]: true,
              }));
            }}
          />
        );
      }
      return item.label;
    },
    [projectFontSet, fontPreviewErrors],
  );

  return (
    <FontFamilyCombobox
      value={normalizedFontFamily ?? ""}
      onChange={setDisplayValue}
      placeholder={placeholder}
      renderItemLabel={renderItemLabel}
    />
  );
};

export default FontSelector;
